import { uuidModule } from "@/modules/uuid";
import {
  AudioRecordingAppEvent,
  AudioRecordingAppEventKind,
  AudioRecordingModelData,
  AudioRecordingSyncUpdateValue,
} from "@/store/audio-recordings/types";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import {
  IAudioRecordingAppEventOperation,
  AudioRecordingAppEventOperationKind,
} from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncCustomErrorData } from "@/store/sync/types";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { AppStore } from "@/store/AppStore";
import { BaseSyncOperationGeneric } from "@/store/sync/operations/BaseSyncOperationGeneric";
import { getLoggableOperation } from "@/store/sync/utils";
import { Optional } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";

export interface BaseAudioRecordingAppEventOperationParams {
  store: AppStore;
  payload: Optional<IAudioRecordingAppEventOperation["payload"], "schema_version">;
  operationId?: Uuid;
  committedAt?: string;
  latestSpaceAccountSequenceId?: number;
  triggerSuccessToast?: boolean;
}

const getOptimisticAppEventKind = ({
  operationKind,
}: {
  operationKind: AudioRecordingAppEventOperationKind;
}): AudioRecordingAppEventKind | null => {
  switch (operationKind) {
    case "MARK_STARTED_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.STARTED_LOCAL;
    case "MARK_PAUSED_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.PAUSED_LOCAL;
    case "MARK_RESUMED_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.RESUMED_LOCAL;
    case "MARK_COMPLETED_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.COMPLETED_LOCAL;
    case "MARK_ERROR_STARTING_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.ERROR_STARTING_LOCAL;
    case "MARK_ERROR_RESUMING_LOCAL_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.ERROR_RESUMING_LOCAL;
    case "MARK_STARTED_UPLOADING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.STARTED_UPLOADING;
    case "MARK_COMPLETED_UPLOADING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.COMPLETED_UPLOADING;
    case "MARK_SKIPPED_UPLOADING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.SKIPPED_UPLOADING;
    case "MARK_ERROR_UPLOADING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.ERROR_UPLOADING;
    case "MARK_TRANSCRIPTION_DISCONNECTED_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.TRANSCRIPTION_DISCONNECTED;
    case "MARK_TRANSCRIPTION_RECONNECTED_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.TRANSCRIPTION_RECONNECTED;
    case "MARK_TRANSCRIPTION_COMPROMISED_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.TRANSCRIPTION_COMPROMISED;
    case "MARK_VOICE_DISCONNECTED_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.VOICE_DISCONNECTED;
    case "MARK_VOICE_RECONNECTED_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.VOICE_RECONNECTED;
    case "MARK_STARTED_RETRANSCRIBING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.STARTED_RETRANSCRIBING;
    case "MARK_COMPLETED_RETRANSCRIBING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.COMPLETED_RETRANSCRIBING;
    case "MARK_ERROR_RETRANSCRIBING_AUDIO_RECORDING":
      return AudioRecordingAppEventKind.ERROR_RETRANSCRIBING;
    case "MARK_STARTED_PROCESSING_AUDIO_RECORDING":
    case "MARK_ERROR_PROCESSING_AUDIO_RECORDING":
    case "MARK_COMPLETED_PROCESSING_AUDIO_RECORDING":
    case "MARK_UNEXPECTED_ERROR_AUDIO_RECORDING":
      return null;
  }
};

/**
 * Base class for audio recording app event sync operations.
 * These operations track events during the recording lifecycle and
 * replace the generic UPDATE_AUDIO_RECORDING operation for status changes.
 *
 * Each operation appends an app event to the recording's value_json.app_events
 * on the backend, providing granular observability into the recording lifecycle.
 */
export abstract class BaseAudioRecordingAppEventOperation extends BaseSyncOperationGeneric<IAudioRecordingAppEventOperation> {
  protected store: AppStore;
  abstract get operationKind(): AudioRecordingAppEventOperationKind;

  constructor({
    store,
    payload,
    operationId,
    committedAt,
    latestSpaceAccountSequenceId,
    triggerSuccessToast,
  }: BaseAudioRecordingAppEventOperationParams) {
    super({
      store,
      payload,
      operationId,
      committedAt,
      latestSpaceAccountSequenceId,
      triggerSuccessToast,
    });
    this.store = store;
  }

  /**
   * @sealed YOU PROBABLY DON'T WANT TO OVERRIDE THIS.
   */
  public async generateSyncOperation(): Promise<IAudioRecordingAppEventOperation> {
    const operation = {
      id: this.operationId,
      client_id: await clientEnvModule.clientId(),
      locally_committed_at: this.committedAt,
      operation_kind: this.operationKind,
      payload: this.payload,
    } as IAudioRecordingAppEventOperation;

    return operation;
  }

  get successToastMessage(): React.ReactNode {
    return null;
  }

  public async execute(): Promise<void> {
    const syncOp = await this.generateSyncOperation();
    if (!syncOp) {
      logger.error({
        message: "[BaseAudioRecordingAppEventOperation] Could not generate sync operation",
        info: { operation: getLoggableOperation(this) },
      });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await this.store.sync.actionQueue.push(this as any);
    await this.applyOptimisticUpdates(syncOp.id);
    await this.triggerRecompute();

    if (this.triggerSuccessToast && this.successToastMessage) {
      toastModule.triggerToast({ content: this.successToastMessage });
    }
  }

  protected async applyOptimisticUpdates(syncOpId: string) {
    const optimisticUpdates = await this.generateOptimisticUpdates();
    for (const optimisticUpdate of optimisticUpdates) {
      await this.store.sync.actionQueue.applyOptimisticUpdate(syncOpId, optimisticUpdate);
    }
  }

  /**
   * Override in subclasses to update model data based on the event.
   * Returns partial model data to merge with existing recording data.
   */
  protected getModelDataUpdates(
    _existingData: AudioRecordingModelData
  ): Partial<AudioRecordingModelData> {
    return {};
  }

  private buildOptimisticValueJson({
    existingData,
  }: {
    existingData: AudioRecordingModelData;
  }): AudioRecordingModelData["value_json"] | undefined {
    const appEventKind = getOptimisticAppEventKind({
      operationKind: this.operationKind,
    });

    if (!appEventKind) {
      return existingData.value_json;
    }

    const valueJson = (existingData.value_json ?? {}) as {
      app_events?: AudioRecordingAppEvent[];
      [key: string]: unknown;
    };
    const existingAppEvents = Array.isArray(valueJson.app_events) ? valueJson.app_events : [];
    const properties =
      this.payload.properties &&
      typeof this.payload.properties === "object" &&
      !Array.isArray(this.payload.properties)
        ? (this.payload.properties as Record<string, unknown>)
        : undefined;
    const nextAppEvent: AudioRecordingAppEvent = {
      kind: appEventKind,
      occurred_at: this.payload.occurred_at,
      properties,
    };

    return {
      ...valueJson,
      app_events: [...existingAppEvents, nextAppEvent],
    };
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<AudioRecordingModelData>[]> {
    await this.store.audioRecordings.updateObservable(this.payload.id);
    const recording = await this.store.audioRecordings.getAsync(this.payload.id);

    if (!recording) return [];

    const modelDataUpdates = this.getModelDataUpdates(recording.modelData);
    const nextValueJson = this.buildOptimisticValueJson({
      existingData: recording.modelData,
    });

    const value: AudioRecordingSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "AUDIO_RECORDING",
      model_version: recording.modelVersion,
      model_data: {
        ...recording.modelData,
        ...modelDataUpdates,
        value_json: nextValueJson,
        locally_modified_at: this.committedAt ?? recording.modelData.locally_modified_at,
      },
      model_scopes: recording.modelScopes,
    };

    const optimisticUpdate: OptimisticSyncUpdate<AudioRecordingModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };

    return [optimisticUpdate];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.audioRecordings.recompute(this.payload.id);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
